import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { PencilIcon, EraserIcon, DownloadIcon, RefreshCwIcon, GridIcon, ShareIcon, TwitterIcon, FacebookIcon, XIcon, CheckIcon, ImageIcon, CopyIcon, ZapIcon, BrainIcon, EyeIcon, RotateCcwIcon, RotateCwIcon, ScanLineIcon, FlaskConicalIcon, LayersIcon, SparklesIcon, NetworkIcon, MaximizeIcon, MinimizeIcon, SettingsIcon, SaveIcon, UserIcon, MessageCircleIcon, BookIcon } from 'lucide-react';
import { useSound } from './SoundEffects';
import { useAvatar } from '../context/AvatarContext';
export const DrawingCanvas = ({
  onCursorChange
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [color, setColor] = useState('#00FFDD');
  const [pixelSize, setPixelSize] = useState(10); // Size of each pixel
  const [tool, setTool] = useState('pencil');
  const [showGrid, setShowGrid] = useState(true);
  const [canvasReady, setCanvasReady] = useState(false);
  const [showChallenge, setShowChallenge] = useState(true);
  const [showReferenceImage, setShowReferenceImage] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showChallengeCompleteMessage, setShowChallengeCompleteMessage] = useState(false);
  const [neuralAnalysis, setNeuralAnalysis] = useState(false);
  const [analysisScore, setAnalysisScore] = useState(0);
  const [analysisData, setAnalysisData] = useState({});
  const [activeFilter, setActiveFilter] = useState(null);
  const [showAIFeedback, setShowAIFeedback] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [neuralProgress, setNeuralProgress] = useState(0);
  const [showSymmetryGrid, setShowSymmetryGrid] = useState(false);
  const [symmetryMode, setSymmetryMode] = useState('none'); // none, horizontal, vertical, quad
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [pixelsFilled, setPixelsFilled] = useState(0);
  const [showParticles, setShowParticles] = useState(false);
  const [pixelDensity, setPixelDensity] = useState('medium'); // low, medium, high
  const [showEntityInfluence, setShowEntityInfluence] = useState(false);
  const [showEndorsements, setShowEndorsements] = useState(false);
  const [showMethodologies, setShowMethodologies] = useState(false);
  const {
    playSound
  } = useSound();
  const {
    avatar
  } = useAvatar();
  const contextRef = useRef(null);
  const pixelGridRef = useRef(null); // Store the pixel grid data
  const particlesRef = useRef([]);
  const containerRef = useRef(null);
  // Fixed canvas dimensions
  const CANVAS_WIDTH = 720;
  const CANVAS_HEIGHT = 1280;
  // Base grid dimensions (9:16 aspect ratio - portrait mode)
  const GRID_RATIOS = {
    low: {
      width: 18,
      height: 32
    },
    medium: {
      width: 27,
      height: 48
    },
    high: {
      width: 36,
      height: 64
    }
  };
  // Get current grid dimensions based on pixel density
  const getCurrentGridDimensions = () => {
    return GRID_RATIOS[pixelDensity];
  };
  // Current grid dimensions
  const [gridDimensions, setGridDimensions] = useState(getCurrentGridDimensions());
  // Enhanced vibrant color palette - now entity-influenced
  const getEntityInfluencedColors = () => {
    // Default colors
    const defaultColors = ['#00FFDD', '#FF00FF', '#FF3D81', '#FFFFFF', '#7B61FF', '#FFF740', '#00FF94', '#FF5C29' // orange
    ];
    // If no avatar is selected, return default colors
    if (!avatar) return defaultColors;
    // Modify color palette based on selected entity
    switch (avatar.id) {
      case 'zendaya':
        return [avatar.accent, '#FF00FF', '#FF3D81', '#FFFFFF', '#7B61FF', '#FFF740', '#00FF94', '#FF5C29'];
      case 'michael':
        return ['#00FFDD', '#FF00FF', avatar.accent, '#FFFFFF', '#7B61FF', '#FFF740', '#00FF94', '#FF5C29'];
      case 'keanu':
        return ['#00FFDD', '#FF00FF', '#FF3D81', avatar.accent, '#7B61FF', '#FFF740', '#00FF94', '#FF5C29'];
      default:
        return defaultColors;
    }
  };
  const colors = getEntityInfluencedColors();
  // Advanced filter options
  const filters = [{
    id: 'glitch',
    name: 'GLITCH',
    icon: <ZapIcon size={14} data-id="element-594" />
  }, {
    id: 'scan',
    name: 'SCAN',
    icon: <ScanLineIcon size={14} data-id="element-595" />
  }, {
    id: 'neural',
    name: 'NEURAL',
    icon: <BrainIcon size={14} data-id="element-596" />
  }, {
    id: 'glow',
    name: 'GLOW',
    icon: <SparklesIcon size={14} data-id="element-597" />
  }];
  // Reference images for the orchid challenge
  const orchidReferenceImages = ['https://uploadthingy.s3.us-west-1.amazonaws.com/sb6h6Ejben2GShAZPsdmoM/orchid_1.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/3gb9Ls8RpzSvGtuRxx3LuG/orchid_2.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/oW6453F3WFauTTR24kJ425/orchid_3.jpg'];
  // Endorsement quotes from industry leaders - integrated from About section
  const endorsements = [{
    quote: "A visionary at the intersection of biology and technology. CHERYL's work challenges our understanding of what's possible.",
    author: 'Andrew Ng, Founder of DeepLearning.AI'
  }, {
    quote: 'The biological distortions created here represent a new frontier in artistic expression. Truly groundbreaking.',
    author: 'Satya Nadella, Chairman and CEO of Microsoft'
  }, {
    quote: "CHERYL's work transcends conventional boundaries. A profound reimagining of the relationship between humans and technology.",
    author: 'Deepak Chopra, Founder of Cyberhuman AI Inc.'
  }, {
    quote: 'An extraordinary talent pushing the limits of what we thought possible. The future of biotech art is here.',
    author: 'Arianna Huffington, Founder and CEO of Thrive Global'
  }];
  // Methodologies derived from the About section
  const methodologies = [{
    name: 'DISTORTION',
    description: 'Manipulating natural forms to reveal hidden patterns and structures'
  }, {
    name: 'MUTATION',
    description: 'Evolutionary transformation of biological concepts into digital networks'
  }, {
    name: 'CORRUPTION',
    description: 'Deliberate degradation of perfect forms to expose underlying truths'
  }, {
    name: 'SYNTHESIS',
    description: 'Merging organic and technological elements into new hybrid forms'
  }, {
    name: 'DECAY',
    description: 'Exploring the beauty in breakdown and dissolution of structures'
  }];
  // AI feedback suggestions - now entity-influenced and methodology-informed
  const getAiSuggestions = () => {
    const defaultSuggestions = ['Increase network density in central nodes for better pattern recognition.', 'Current orchid structure shows strong biological signatures.', 'Consider adding magenta highlights to enhance the neural pathways.', 'Your composition has 87% network coherence. Excellent work.', 'Try creating more connections between the upper and lower segments.', 'The network topology suggests high computational efficiency.', 'Add symmetrical nodes for enhanced pattern stability.', 'Current structure shows potential for 43% increased data throughput.', "The orchid's neural framework exhibits remarkable adaptive properties.", 'Consider creating gradient pathways between major network junctions.'];
    // If methodologies panel was recently viewed, include methodology-based suggestions
    if (showMethodologies) {
      const methodology = methodologies[Math.floor(Math.random() * methodologies.length)];
      return [...defaultSuggestions, `Apply the ${methodology.name} methodology to create more compelling network structures.`, `Your pattern shows strong ${methodology.name} influence. Consider enhancing this aspect.`, `The ${methodology.name} approach would suggest adding more connections in the peripheral nodes.`, `According to ${methodology.name} principles, your network could benefit from more asymmetrical elements.`];
    }
    // If entity is selected, include entity-specific suggestions
    if (avatar) {
      return [...defaultSuggestions, `${avatar.name}'s neural signature detected in your pattern structure.`, `This network shows characteristics aligned with ${avatar.name}'s cognitive patterns.`, `Try incorporating more of ${avatar.name}'s distinctive node arrangements.`, `The connection density matches ${avatar.name}'s typical neural architecture.`];
    }
    return defaultSuggestions;
  };
  // Change pixel density
  const changePixelDensity = density => {
    if (density === pixelDensity) return;
    // Save current state for potential restoration
    const currentState = pixelGridRef.current ? pixelGridRef.current.map(row => [...row]) : null;
    // Update density setting
    setPixelDensity(density);
    setGridDimensions(GRID_RATIOS[density]);
    // Clear the canvas with new dimensions
    if (pixelGridRef.current) {
      const {
        width,
        height
      } = GRID_RATIOS[density];
      const grid = Array(height).fill().map(() => Array(width).fill('rgba(3, 5, 8, 0.3)'));
      pixelGridRef.current = grid;
      // Redraw
      playSound('click');
      drawPixelGrid();
      countFilledPixels();
    }
  };
  // Initialize canvas and pixel grid
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    // Set fixed canvas dimensions
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    // Scale canvas to fit container while maintaining aspect ratio
    const containerWidth = container.offsetWidth;
    const scale = containerWidth / CANVAS_WIDTH;
    // Set the display size (css pixels)
    canvas.style.width = `${CANVAS_WIDTH * scale}px`;
    canvas.style.height = `${CANVAS_HEIGHT * scale}px`;
    // Get the drawing context
    const context = canvas.getContext('2d');
    context.imageSmoothingEnabled = false; // Disable anti-aliasing for crisp pixels
    contextRef.current = context;
    // Initialize pixel grid (2D array)
    const grid = Array(gridDimensions.height).fill().map(() => Array(gridDimensions.width).fill('rgba(3, 5, 8, 0.3)'));
    pixelGridRef.current = grid;
    // Initialize particles
    initializeParticles();
    // Draw initial grid
    drawPixelGrid();
    setCanvasReady(true);
    // Handle window resize
    const handleResize = () => {
      const newContainerWidth = container.offsetWidth;
      const newScale = newContainerWidth / CANVAS_WIDTH;
      // Update display size while keeping the same canvas dimensions
      canvas.style.width = `${CANVAS_WIDTH * newScale}px`;
      canvas.style.height = `${CANVAS_HEIGHT * newScale}px`;
      drawPixelGrid();
    };
    window.addEventListener('resize', handleResize);
    // Animation loop for particles
    let animationFrameId;
    if (showParticles) {
      const animate = () => {
        updateParticles();
        drawPixelGrid(); // This will also draw the particles
        animationFrameId = requestAnimationFrame(animate);
      };
      animate();
    }
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [showParticles, gridDimensions]);
  // Initialize particles
  const initializeParticles = () => {
    const particles = [];
    const {
      width,
      height
    } = gridDimensions;
    for (let i = 0; i < 80; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.08,
        vy: (Math.random() - 0.5) * 0.08,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 0.5 + 0.2,
        life: Math.random() * 100 + 50
      });
    }
    particlesRef.current = particles;
  };
  // Update particles
  const updateParticles = () => {
    const {
      width,
      height
    } = gridDimensions;
    particlesRef.current = particlesRef.current.map(p => {
      // Update position
      p.x += p.vx;
      p.y += p.vy;
      p.life -= 1;
      // If particle dies, reset it
      if (p.life <= 0) {
        return {
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.08,
          vy: (Math.random() - 0.5) * 0.08,
          color: colors[Math.floor(Math.random() * colors.length)],
          size: Math.random() * 0.5 + 0.2,
          life: Math.random() * 100 + 50
        };
      }
      // Bounce off edges
      if (p.x < 0 || p.x > width) p.vx *= -1;
      if (p.y < 0 || p.y > height) p.vy *= -1;
      return p;
    });
  };
  // Draw the entire pixel grid based on current state
  const drawPixelGrid = () => {
    if (!contextRef.current || !pixelGridRef.current) return;
    const canvas = canvasRef.current;
    const ctx = contextRef.current;
    const {
      width,
      height
    } = gridDimensions;
    // Calculate cell size for perfect squares
    const cellSizeX = CANVAS_WIDTH / width;
    const cellSizeY = CANVAS_HEIGHT / height;
    // Clear canvas
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    // Draw background
    ctx.fillStyle = 'rgba(3, 5, 8, 0.95)';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    // Draw each pixel
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const pixelColor = pixelGridRef.current[y][x];
        // Skip transparent pixels
        if (pixelColor === 'rgba(3, 5, 8, 0.3)') {
          ctx.fillStyle = pixelColor;
          ctx.fillRect(x * cellSizeX, y * cellSizeY, cellSizeX, cellSizeY);
          continue;
        }
        // Apply active filter effects
        if (activeFilter) {
          ctx.save();
          ctx.fillStyle = pixelColor;
          ctx.fillRect(x * cellSizeX, y * cellSizeY, cellSizeX, cellSizeY);
          // Apply different filter effects
          switch (activeFilter) {
            case 'glitch':
              // Random glitch effect
              if (Math.random() > 0.95) {
                ctx.fillStyle = colors[Math.floor(Math.random() * colors.length)];
                const offsetX = Math.random() * 2 - 1;
                const offsetY = Math.random() * 2 - 1;
                ctx.fillRect((x + offsetX) * cellSizeX, (y + offsetY) * cellSizeY, cellSizeX, cellSizeY);
              }
              break;
            case 'scan':
              // Scan line effect
              const scanY = Date.now() / 20 % CANVAS_HEIGHT;
              if (y * cellSizeY < scanY && y * cellSizeY + cellSizeY > scanY) {
                ctx.fillStyle = '#00FFDD';
                ctx.globalAlpha = 0.8;
                ctx.fillRect(x * cellSizeX, y * cellSizeY, cellSizeX, cellSizeY);
              }
              break;
            case 'neural':
              // Neural network visualization effect
              if (Math.random() > 0.9) {
                ctx.strokeStyle = '#FF00FF80';
                ctx.beginPath();
                ctx.moveTo(x * cellSizeX + cellSizeX / 2, y * cellSizeY + cellSizeY / 2);
                const targetX = Math.floor(Math.random() * width) * cellSizeX + cellSizeX / 2;
                const targetY = Math.floor(Math.random() * height) * cellSizeY + cellSizeY / 2;
                ctx.lineTo(targetX, targetY);
                ctx.stroke();
              }
              break;
            case 'glow':
              // Glow effect
              const gradient = ctx.createRadialGradient(x * cellSizeX + cellSizeX / 2, y * cellSizeY + cellSizeY / 2, 0, x * cellSizeX + cellSizeX / 2, y * cellSizeY + cellSizeY / 2, cellSizeX * 1.5);
              gradient.addColorStop(0, pixelColor);
              gradient.addColorStop(1, 'transparent');
              ctx.fillStyle = gradient;
              ctx.globalAlpha = 0.4;
              ctx.fillRect(x * cellSizeX - cellSizeX, y * cellSizeY - cellSizeY, cellSizeX * 3, cellSizeY * 3);
              break;
            default:
              break;
          }
          ctx.restore();
        } else {
          // Normal rendering without filter
          ctx.fillStyle = pixelColor;
          ctx.fillRect(x * cellSizeX, y * cellSizeY, cellSizeX, cellSizeY);
        }
      }
    }
    // Draw particles if enabled
    if (showParticles && particlesRef.current) {
      particlesRef.current.forEach(p => {
        ctx.fillStyle = p.color + Math.floor(p.life / 5).toString(16).padStart(2, '0');
        ctx.fillRect(p.x * cellSizeX, p.y * cellSizeY, p.size * cellSizeX, p.size * cellSizeY);
      });
    }
    // Draw symmetry guidelines if enabled
    if (showSymmetryGrid) {
      ctx.strokeStyle = 'rgba(255, 0, 255, 0.4)';
      ctx.lineWidth = 1;
      if (symmetryMode === 'horizontal' || symmetryMode === 'quad') {
        // Horizontal symmetry line
        ctx.beginPath();
        ctx.moveTo(0, CANVAS_HEIGHT / 2);
        ctx.lineTo(CANVAS_WIDTH, CANVAS_HEIGHT / 2);
        ctx.stroke();
      }
      if (symmetryMode === 'vertical' || symmetryMode === 'quad') {
        // Vertical symmetry line
        ctx.beginPath();
        ctx.moveTo(CANVAS_WIDTH / 2, 0);
        ctx.lineTo(CANVAS_WIDTH / 2, CANVAS_HEIGHT);
        ctx.stroke();
      }
    }
    // Draw grid lines if enabled
    if (showGrid) {
      ctx.strokeStyle = 'rgba(0, 255, 221, 0.3)';
      ctx.lineWidth = 0.5;
      // Draw vertical lines
      for (let x = 0; x <= width; x++) {
        ctx.beginPath();
        ctx.moveTo(x * cellSizeX, 0);
        ctx.lineTo(x * cellSizeX, CANVAS_HEIGHT);
        ctx.stroke();
      }
      // Draw horizontal lines
      for (let y = 0; y <= height; y++) {
        ctx.beginPath();
        ctx.moveTo(0, y * cellSizeY);
        ctx.lineTo(CANVAS_WIDTH, y * cellSizeY);
        ctx.stroke();
      }
    }
  };
  // Convert canvas coordinates to grid coordinates
  const getGridCoordinates = (x, y) => {
    const canvas = canvasRef.current;
    const {
      width,
      height
    } = gridDimensions;
    // Calculate the scale factor based on the displayed size vs actual canvas size
    const scaleX = CANVAS_WIDTH / parseFloat(canvas.style.width);
    const scaleY = CANVAS_HEIGHT / parseFloat(canvas.style.height);
    // Apply scale to get the actual canvas coordinates
    const canvasX = x * scaleX;
    const canvasY = y * scaleY;
    // Calculate cell size for perfect squares
    const cellSizeX = CANVAS_WIDTH / width;
    const cellSizeY = CANVAS_HEIGHT / height;
    const gridX = Math.floor(canvasX / cellSizeX);
    const gridY = Math.floor(canvasY / cellSizeY);
    return {
      x: Math.max(0, Math.min(gridX, width - 1)),
      y: Math.max(0, Math.min(gridY, height - 1))
    };
  };
  // Set a pixel at grid coordinates with symmetry support
  const setPixel = (gridX, gridY) => {
    if (!pixelGridRef.current) return;
    const {
      width,
      height
    } = gridDimensions;
    // Bounds check
    if (gridX < 0 || gridX >= width || gridY < 0 || gridY >= height) return;
    // Save the current state for undo
    saveCurrentStateForUndo();
    // Play sound when drawing
    playSound('click');
    // Set pixel color based on current tool
    const pixelColor = tool === 'eraser' ? 'rgba(3, 5, 8, 0.3)' : color;
    pixelGridRef.current[gridY][gridX] = pixelColor;
    // Apply symmetry if enabled
    if (symmetryMode !== 'none') {
      const centerX = Math.floor(width / 2);
      const centerY = Math.floor(height / 2);
      if (symmetryMode === 'horizontal' || symmetryMode === 'quad') {
        // Mirror across horizontal axis
        const mirrorY = height - 1 - gridY;
        pixelGridRef.current[mirrorY][gridX] = pixelColor;
      }
      if (symmetryMode === 'vertical' || symmetryMode === 'quad') {
        // Mirror across vertical axis
        const mirrorX = width - 1 - gridX;
        pixelGridRef.current[gridY][mirrorX] = pixelColor;
      }
      if (symmetryMode === 'quad') {
        // Mirror across both axes (fourth point in quad)
        const mirrorX = width - 1 - gridX;
        const mirrorY = height - 1 - gridY;
        pixelGridRef.current[mirrorY][mirrorX] = pixelColor;
      }
    }
    // Count filled pixels for analytics
    countFilledPixels();
    // Redraw the grid
    drawPixelGrid();
    // Randomly show AI feedback
    if (Math.random() > 0.95 && !showAIFeedback) {
      generateAIFeedback();
    }
  };
  // Count non-background pixels
  const countFilledPixels = () => {
    const {
      width,
      height
    } = gridDimensions;
    let count = 0;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (pixelGridRef.current[y][x] !== 'rgba(3, 5, 8, 0.3)') {
          count++;
        }
      }
    }
    setPixelsFilled(count);
    return count;
  };
  // Save current state for undo
  const saveCurrentStateForUndo = () => {
    // Create a deep copy of the current grid
    const currentState = pixelGridRef.current.map(row => [...row]);
    setUndoStack([...undoStack, currentState]);
    // Clear redo stack when new actions are performed
    setRedoStack([]);
  };
  // Undo last action
  const undo = () => {
    if (undoStack.length === 0) return;
    // Save current state to redo stack
    const currentState = pixelGridRef.current.map(row => [...row]);
    setRedoStack([...redoStack, currentState]);
    // Pop the last state from undo stack
    const newUndoStack = [...undoStack];
    const lastState = newUndoStack.pop();
    setUndoStack(newUndoStack);
    // Restore the previous state
    pixelGridRef.current = lastState;
    drawPixelGrid();
    countFilledPixels();
    playSound('click');
  };
  // Redo last undone action
  const redo = () => {
    if (redoStack.length === 0) return;
    // Save current state to undo stack
    const currentState = pixelGridRef.current.map(row => [...row]);
    setUndoStack([...undoStack, currentState]);
    // Pop the last state from redo stack
    const newRedoStack = [...redoStack];
    const nextState = newRedoStack.pop();
    setRedoStack(newRedoStack);
    // Restore the next state
    pixelGridRef.current = nextState;
    drawPixelGrid();
    countFilledPixels();
    playSound('click');
  };
  const startDrawing = ({
    nativeEvent
  }) => {
    const {
      offsetX,
      offsetY
    } = nativeEvent;
    const {
      x,
      y
    } = getGridCoordinates(offsetX, offsetY);
    setPixel(x, y);
    setIsDrawing(true);
  };
  const draw = ({
    nativeEvent
  }) => {
    if (!isDrawing) return;
    const {
      offsetX,
      offsetY
    } = nativeEvent;
    const {
      x,
      y
    } = getGridCoordinates(offsetX, offsetY);
    setPixel(x, y);
  };
  const stopDrawing = () => {
    setIsDrawing(false);
  };
  const clearCanvas = () => {
    playSound('error');
    // Save current state for undo
    saveCurrentStateForUndo();
    const {
      width,
      height
    } = gridDimensions;
    // Reset all pixels to background color
    const grid = Array(height).fill().map(() => Array(width).fill('rgba(3, 5, 8, 0.3)'));
    pixelGridRef.current = grid;
    // Redraw the grid
    drawPixelGrid();
    // Reset challenge complete message
    setShowChallengeCompleteMessage(false);
    setPixelsFilled(0);
    // Reset analysis
    setNeuralAnalysis(false);
    setAnalysisScore(0);
    setAnalysisData({});
    setShowAIFeedback(false);
  };
  const saveCanvas = () => {
    playSound('success');
    const canvas = canvasRef.current;
    // Create a temporary canvas without grid lines for saving
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const tempCtx = tempCanvas.getContext('2d');
    tempCtx.imageSmoothingEnabled = false;
    // Draw pixels to temp canvas
    const {
      width,
      height
    } = gridDimensions;
    const cellSizeX = canvas.width / width;
    const cellSizeY = canvas.height / height;
    // Draw background
    tempCtx.fillStyle = '#030508';
    tempCtx.fillRect(0, 0, canvas.width, canvas.height);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        tempCtx.fillStyle = pixelGridRef.current[y][x];
        tempCtx.fillRect(x * cellSizeX, y * cellSizeY, cellSizeX, cellSizeY);
      }
    }
    // Create download link
    const image = tempCanvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = image;
    link.download = 'networks-orchid-mode.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Set share link
    setShareLink(image);
  };
  const toggleGrid = () => {
    playSound('click');
    setShowGrid(!showGrid);
    drawPixelGrid();
  };
  const toggleReferenceImage = () => {
    playSound('click');
    setShowReferenceImage(!showReferenceImage);
  };
  const toggleParticles = () => {
    playSound('click');
    setShowParticles(!showParticles);
  };
  const toggleSymmetryGrid = () => {
    playSound('click');
    if (!showSymmetryGrid) {
      setShowSymmetryGrid(true);
      setSymmetryMode('horizontal');
    } else {
      // Cycle through symmetry modes
      if (symmetryMode === 'horizontal') {
        setSymmetryMode('vertical');
      } else if (symmetryMode === 'vertical') {
        setSymmetryMode('quad');
      } else if (symmetryMode === 'quad') {
        setShowSymmetryGrid(false);
        setSymmetryMode('none');
      }
    }
    drawPixelGrid();
  };
  const shareToTwitter = () => {
    playSound('click');
    // Save canvas first to generate the image
    saveCanvas();
    // Open Twitter share dialog
    const text = "I created a neural orchid network in 'Networks: Orchid Mode' on Cheryl Qian's Biological Distortion site! #NeuralNetworks #DigitalArt";
    const url = window.location.href;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };
  const shareToFacebook = () => {
    playSound('click');
    // Open Facebook share dialog
    const url = window.location.href;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
  };
  const copyShareLink = () => {
    playSound('success');
    // Generate image if not already done
    if (!shareLink) {
      saveCanvas();
    }
    // Copy the current URL to clipboard
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }, err => {
      console.error('Could not copy text: ', err);
    });
  };
  const completeChallenge = () => {
    playSound('success');
    setShowChallengeCompleteMessage(true);
    setShowChallenge(false);
    // Trigger neural analysis
    runNeuralAnalysis();
  };
  // Generate AI feedback
  const generateAIFeedback = () => {
    const suggestions = getAiSuggestions();
    const randomSuggestion = suggestions[Math.floor(Math.random() * suggestions.length)];
    setAiSuggestion(randomSuggestion);
    setShowAIFeedback(true);
    // Auto-hide feedback after a delay
    setTimeout(() => {
      setShowAIFeedback(false);
    }, 8000);
  };
  // Toggle entity influence panel
  const toggleEntityInfluence = () => {
    playSound('click');
    setShowEntityInfluence(!showEntityInfluence);
    setShowEndorsements(false);
    setShowMethodologies(false);
  };
  // Toggle endorsements panel
  const toggleEndorsements = () => {
    playSound('click');
    setShowEndorsements(!showEndorsements);
    setShowEntityInfluence(false);
    setShowMethodologies(false);
  };
  // Toggle methodologies panel
  const toggleMethodologies = () => {
    playSound('click');
    setShowMethodologies(!showMethodologies);
    setShowEntityInfluence(false);
    setShowEndorsements(false);
  };
  const handleToolChange = newTool => {
    playSound('click');
    setTool(newTool);
  };
  const handleColorSelect = selectedColor => {
    playSound('click');
    setColor(selectedColor);
  };
  const toggleShareOptions = () => {
    playSound('click');
    setShowShareOptions(!showShareOptions);
  };
  const applyFilter = filterId => {
    playSound('click');
    setActiveFilter(activeFilter === filterId ? null : filterId);
    drawPixelGrid();
  };
  return <div className="drawing-canvas-container w-full mb-8 mt-2" data-id="element-598">
      <style jsx global data-id="element-599">{`
        .drawing-canvas {
          border: 1px solid rgba(0, 255, 221, 0.4);
          border-radius: 4px;
          background: rgba(3, 5, 8, 0.95);
          cursor: crosshair !important;
          transition: all 0.3s ease;
          image-rendering: pixelated;
          image-rendering: crisp-edges;
          position: relative;
          overflow: hidden;
          width: 100%;
          max-height: 70vh;
          margin: 0 auto;
        }
        .drawing-canvas:hover {
          border-color: rgba(0, 255, 221, 0.7);
          box-shadow: 0 0 20px rgba(0, 255, 221, 0.3);
        }
        .drawing-canvas::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(0, 255, 221, 0.7),
            transparent
          );
          z-index: 5;
          animation: scan-line 4s linear infinite;
          opacity: 0.7;
        }
        @keyframes scan-line {
          0% {
            transform: translateY(-100%);
          }
          100% {
            transform: translateY(1000%);
          }
        }
        .drawing-controls {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 12px;
          flex-wrap: wrap;
          position: relative;
          justify-content: center;
        }
        .drawing-controls::before {
          content: '';
          position: absolute;
          top: -4px;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(
            90deg,
            rgba(0, 255, 221, 0),
            rgba(0, 255, 221, 0.4),
            rgba(0, 255, 221, 0)
          );
        }
        .drawing-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          border-radius: 4px;
          background: rgba(0, 255, 221, 0.1);
          color: #00ffdd;
          border: 1px solid rgba(0, 255, 221, 0.3);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }
        .drawing-button:hover {
          background: rgba(0, 255, 221, 0.2);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }
        .drawing-button.active {
          background: rgba(0, 255, 221, 0.3);
          box-shadow: 0 0 15px rgba(0, 255, 221, 0.4);
        }
        .drawing-button::after {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(
            circle,
            rgba(0, 255, 221, 0.2) 0%,
            transparent 70%
          );
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .drawing-button:hover::after {
          opacity: 1;
        }
        .drawing-button.active::after {
          opacity: 1;
          animation: pulse-glow 2s infinite;
        }
        .tool-group {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px;
          background: rgba(3, 5, 8, 0.7);
          border-radius: 6px;
          border: 1px solid rgba(0, 255, 221, 0.15);
        }
        .color-picker {
          display: flex;
          gap: 4px;
          padding: 4px;
          background: rgba(3, 5, 8, 0.7);
          border-radius: 6px;
          border: 1px solid rgba(0, 255, 221, 0.15);
        }
        .color-option {
          width: 24px;
          height: 24px;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.3s ease;
          border: 2px solid rgba(255, 255, 255, 0.1);
          position: relative;
          overflow: hidden;
        }
        .color-option:hover {
          transform: scale(1.1);
          box-shadow: 0 0 10px currentColor;
        }
        .color-option.active {
          border-color: rgba(255, 255, 255, 0.9);
          box-shadow: 0 0 10px currentColor;
        }
        .color-option::after {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(
            circle,
            rgba(255, 255, 255, 0.3) 0%,
            transparent 70%
          );
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .color-option:hover::after {
          opacity: 1;
        }
        .drawing-instruction {
          text-align: center;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.75rem;
          color: #a0c0e0;
          margin-top: 8px;
          opacity: 0.8;
          position: relative;
          letter-spacing: 0.5px;
        }
        .drawing-instruction::before,
        .drawing-instruction::after {
          content: '';
          position: absolute;
          top: 50%;
          width: 20px;
          height: 1px;
          background: rgba(0, 255, 221, 0.4);
        }
        .drawing-instruction::before {
          left: 25%;
        }
        .drawing-instruction::after {
          right: 25%;
        }
        .pixel-info {
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.75rem;
          color: #a0c0e0;
          text-align: center;
          margin-bottom: 8px;
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 12px;
        }
        .pixel-count {
          color: #00ffdd;
          background: rgba(0, 255, 221, 0.1);
          padding: 2px 8px;
          border-radius: 4px;
          border: 1px solid rgba(0, 255, 221, 0.3);
        }
        .resolution-selector {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
          justify-content: center;
        }
        .resolution-button {
          padding: 3px 10px;
          font-size: 0.7rem;
          font-family: 'JetBrains Mono', monospace;
          background: rgba(3, 5, 8, 0.7);
          border: 1px solid rgba(0, 255, 221, 0.2);
          color: #a0c0e0;
          border-radius: 4px;
          transition: all 0.3s ease;
        }
        .resolution-button:hover {
          background: rgba(0, 255, 221, 0.1);
          color: #00ffdd;
        }
        .resolution-button.active {
          background: rgba(0, 255, 221, 0.2);
          color: #00ffdd;
          border-color: rgba(0, 255, 221, 0.4);
        }
        .challenge-modal {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(3, 5, 8, 0.95);
          border: 1px solid rgba(0, 255, 221, 0.4);
          border-radius: 8px;
          padding: 20px;
          width: 90%;
          max-width: 400px;
          z-index: 100;
          box-shadow: 0 0 30px rgba(0, 255, 221, 0.3);
          backdrop-filter: blur(10px);
        }
        .challenge-modal::before {
          content: '';
          position: absolute;
          top: -1px;
          left: -1px;
          right: -1px;
          bottom: -1px;
          background: linear-gradient(
            45deg,
            rgba(0, 255, 221, 0.6),
            rgba(255, 0, 255, 0.6)
          );
          border-radius: 9px;
          z-index: -1;
          opacity: 0.2;
          animation: border-glow 3s infinite alternate;
        }
        @keyframes border-glow {
          0% {
            opacity: 0.1;
            filter: blur(3px);
          }
          100% {
            opacity: 0.3;
            filter: blur(1px);
          }
        }
        .reference-modal {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(3, 5, 8, 0.95);
          border: 1px solid rgba(0, 255, 221, 0.4);
          border-radius: 8px;
          padding: 20px;
          width: 90%;
          max-width: 500px;
          z-index: 100;
          box-shadow: 0 0 30px rgba(0, 255, 221, 0.3);
          backdrop-filter: blur(10px);
        }
        .reference-modal::before {
          content: '';
          position: absolute;
          top: -1px;
          left: -1px;
          right: -1px;
          bottom: -1px;
          background: linear-gradient(
            45deg,
            rgba(0, 255, 221, 0.6),
            rgba(255, 0, 255, 0.6)
          );
          border-radius: 9px;
          z-index: -1;
          opacity: 0.2;
          animation: border-glow 3s infinite alternate;
        }
        .reference-image-container {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          margin-top: 15px;
        }
        .reference-image {
          width: 100%;
          height: auto;
          border-radius: 4px;
          border: 1px solid rgba(0, 255, 221, 0.3);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }
        .reference-image:hover {
          transform: scale(1.05);
          border-color: rgba(0, 255, 221, 0.8);
          box-shadow: 0 0 15px rgba(0, 255, 221, 0.4);
        }
        .reference-image::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            135deg,
            rgba(0, 255, 221, 0.3),
            transparent
          );
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .reference-image:hover::after {
          opacity: 1;
        }
        .share-options {
          position: absolute;
          top: 100%;
          right: 0;
          background: rgba(3, 5, 8, 0.95);
          border: 1px solid rgba(0, 255, 221, 0.4);
          border-radius: 8px;
          padding: 10px;
          margin-top: 8px;
          z-index: 100;
          box-shadow: 0 0 30px rgba(0, 255, 221, 0.3);
          backdrop-filter: blur(10px);
          width: 180px;
        }
        .share-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          width: 100%;
          border-radius: 4px;
          background: transparent;
          color: #a0c0e0;
          border: none;
          transition: all 0.3s ease;
          text-align: left;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.75rem;
        }
        .share-button:hover {
          background: rgba(0, 255, 221, 0.1);
          color: #00ffdd;
        }
        .share-button-twitter {
          color: #1da1f2;
        }
        .share-button-facebook {
          color: #4267b2;
        }
        .share-button-copy {
          color: #a0c0e0;
        }
        .success-message {
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0, 255, 221, 0.2);
          border: 1px solid rgba(0, 255, 221, 0.5);
          border-radius: 4px;
          padding: 8px 16px;
          color: #00ffdd;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.75rem;
          display: flex;
          align-items: center;
          gap: 8px;
          animation: fadeInOut 2s forwards;
        }
        @keyframes fadeInOut {
          0% {
            opacity: 0;
            transform: translate(-50%, 20px);
          }
          20% {
            opacity: 1;
            transform: translate(-50%, 0);
          }
          80% {
            opacity: 1;
            transform: translate(-50%, 0);
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -20px);
          }
        }
        .challenge-complete {
          background: rgba(0, 255, 221, 0.1);
          border: 1px solid rgba(0, 255, 221, 0.4);
          border-radius: 4px;
          padding: 10px;
          margin-top: 10px;
          display: flex;
          align-items: center;
          gap: 10px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.75rem;
          color: #00ffdd;
          animation: glowPulse 2s infinite;
          position: relative;
          overflow: hidden;
        }
        .challenge-complete::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(0, 255, 221, 0.3),
            transparent
          );
          animation: shine 2s infinite;
        }
        @keyframes shine {
          100% {
            left: 100%;
          }
        }
        @keyframes glowPulse {
          0%,
          100% {
            box-shadow: 0 0 5px rgba(0, 255, 221, 0.4);
          }
          50% {
            box-shadow: 0 0 15px rgba(0, 255, 221, 0.6);
          }
        }
        .share-challenge-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 8px 16px;
          background: rgba(0, 255, 221, 0.1);
          border: 1px solid rgba(0, 255, 221, 0.4);
          border-radius: 4px;
          color: #00ffdd;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.75rem;
          transition: all 0.3s ease;
          margin-top: 10px;
        }
        .share-challenge-button:hover {
          background: rgba(0, 255, 221, 0.2);
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
        }
        .neural-analysis {
          background: rgba(255, 0, 255, 0.1);
          border: 1px solid rgba(255, 0, 255, 0.4);
          border-radius: 4px;
          padding: 12px;
          margin-top: 10px;
          font-family: 'JetBrains Mono', monospace;
          position: relative;
          overflow: hidden;
        }
        .neural-analysis-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 10px;
        }
        .neural-analysis-title {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #ff00ff;
          font-size: 0.8rem;
          font-weight: bold;
        }
        .neural-analysis-score {
          font-size: 1.2rem;
          color: #ff00ff;
          font-weight: bold;
        }
        .neural-progress {
          height: 4px;
          background: rgba(255, 0, 255, 0.2);
          border-radius: 2px;
          margin: 10px 0;
          overflow: hidden;
        }
        .neural-progress-bar {
          height: 100%;
          background: linear-gradient(90deg, #ff00ff, #00ffdd);
          width: 0%;
          transition: width 0.3s ease;
          position: relative;
          overflow: hidden;
        }
        .neural-progress-bar::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.4),
            transparent
          );
          animation: shimmer 1.5s infinite;
        }
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        .neural-data-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 8px;
          margin-top: 10px;
        }
        .neural-data-item {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .neural-data-label {
          font-size: 0.7rem;
          color: #a0c0e0;
        }
        .neural-data-value {
          font-size: 0.8rem;
          color: #ff00ff;
          font-weight: bold;
        }
        .ai-feedback {
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(255, 0, 255, 0.15);
          border: 1px solid rgba(255, 0, 255, 0.4);
          border-radius: 4px;
          padding: 10px 16px;
          color: #ff00ff;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.75rem;
          display: flex;
          align-items: center;
          gap: 8px;
          animation: fadeInOut 8s forwards;
          z-index: 50;
          max-width: 80%;
          text-align: center;
        }
        .filter-options {
          display: flex;
          gap: 4px;
          padding: 4px;
          background: rgba(3, 5, 8, 0.7);
          border-radius: 6px;
          border: 1px solid rgba(255, 0, 255, 0.15);
        }
        .filter-button {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 4px;
          background: transparent;
          color: #a0c0e0;
          border: 1px solid transparent;
          transition: all 0.3s ease;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.65rem;
        }
        .filter-button:hover {
          background: rgba(255, 0, 255, 0.1);
          color: #ff00ff;
        }
        .filter-button.active {
          background: rgba(255, 0, 255, 0.2);
          color: #ff00ff;
          border-color: rgba(255, 0, 255, 0.4);
          box-shadow: 0 0 10px rgba(255, 0, 255, 0.3);
        }
        .symmetry-indicator {
          position: absolute;
          top: 8px;
          right: 8px;
          background: rgba(255, 0, 255, 0.2);
          color: #ff00ff;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.65rem;
          padding: 2px 6px;
          border-radius: 4px;
          z-index: 10;
        }
        .interface-corner {
          position: absolute;
          width: 12px;
          height: 12px;
          z-index: 5;
        }
        .corner-tl {
          top: 0;
          left: 0;
          border-top: 2px solid rgba(0, 255, 221, 0.7);
          border-left: 2px solid rgba(0, 255, 221, 0.7);
        }
        .corner-tr {
          top: 0;
          right: 0;
          border-top: 2px solid rgba(0, 255, 221, 0.7);
          border-right: 2px solid rgba(0, 255, 221, 0.7);
        }
        .corner-bl {
          bottom: 0;
          left: 0;
          border-bottom: 2px solid rgba(0, 255, 221, 0.7);
          border-left: 2px solid rgba(0, 255, 221, 0.7);
        }
        .corner-br {
          bottom: 0;
          right: 0;
          border-bottom: 2px solid rgba(0, 255, 221, 0.7);
          border-right: 2px solid rgba(0, 255, 221, 0.7);
        }
        /* Entity influence panel styles */
        .entity-influence-panel {
          background: rgba(3, 5, 8, 0.95);
          border: 1px solid rgba(0, 255, 221, 0.4);
          border-radius: 8px;
          padding: 16px;
          margin-top: 16px;
          position: relative;
          overflow: hidden;
        }
        .entity-influence-panel::before {
          content: '';
          position: absolute;
          top: -1px;
          left: -1px;
          right: -1px;
          bottom: -1px;
          background: linear-gradient(
            45deg,
            rgba(0, 255, 221, 0.6),
            rgba(255, 0, 255, 0.6)
          );
          border-radius: 9px;
          z-index: -1;
          opacity: 0.2;
          animation: border-glow 3s infinite alternate;
        }
        .entity-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
        }
        .entity-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid;
        }
        .entity-info {
          flex: 1;
        }
        .entity-name {
          font-family: 'Syne', sans-serif;
          font-weight: bold;
          color: #f0f6ff;
          margin-bottom: 2px;
        }
        .entity-description {
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.7rem;
          color: #a0c0e0;
        }
        .entity-influence-meter {
          height: 4px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 2px;
          margin: 8px 0;
          overflow: hidden;
        }
        .entity-influence-value {
          height: 100%;
          width: 0%;
          transition: width 1s ease;
        }
        .entity-bonus {
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.7rem;
          color: #00ffdd;
          text-align: right;
        }
        /* Endorsement panel styles */
        .endorsement-panel {
          background: rgba(3, 5, 8, 0.95);
          border: 1px solid rgba(255, 0, 255, 0.4);
          border-radius: 8px;
          padding: 16px;
          margin-top: 16px;
          position: relative;
          overflow: hidden;
        }
        .endorsement-panel::before {
          content: '';
          position: absolute;
          top: -1px;
          left: -1px;
          right: -1px;
          bottom: -1px;
          background: linear-gradient(
            45deg,
            rgba(255, 0, 255, 0.6),
            rgba(0, 255, 221, 0.6)
          );
          border-radius: 9px;
          z-index: -1;
          opacity: 0.2;
          animation: border-glow 3s infinite alternate;
        }
        .endorsement-quote {
          font-family: 'Space Grotesk', sans-serif;
          font-style: italic;
          font-size: 0.85rem;
          color: #a0c0e0;
          margin-bottom: 8px;
          line-height: 1.4;
        }
        .endorsement-author {
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.7rem;
          color: #ff00ff;
          text-align: right;
        }
        /* Methodology panel styles */
        .methodology-panel {
          background: rgba(3, 5, 8, 0.95);
          border: 1px solid rgba(255, 255, 0, 0.4);
          border-radius: 8px;
          padding: 16px;
          margin-top: 16px;
          position: relative;
          overflow: hidden;
        }
        .methodology-panel::before {
          content: '';
          position: absolute;
          top: -1px;
          left: -1px;
          right: -1px;
          bottom: -1px;
          background: linear-gradient(
            45deg,
            rgba(255, 255, 0, 0.6),
            rgba(0, 255, 221, 0.6)
          );
          border-radius: 9px;
          z-index: -1;
          opacity: 0.2;
          animation: border-glow 3s infinite alternate;
        }
        .methodology-name {
          font-family: 'Syne', sans-serif;
          font-weight: bold;
          color: #fff740;
          margin-bottom: 4px;
        }
        .methodology-description {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.8rem;
          color: #a0c0e0;
          margin-bottom: 8px;
          line-height: 1.4;
        }
        .methodology-tag {
          display: inline-block;
          padding: 4px 8px;
          background: rgba(255, 255, 0, 0.1);
          border: 1px solid rgba(255, 255, 0, 0.3);
          border-radius: 4px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.7rem;
          color: #fff740;
          margin-right: 8px;
          margin-bottom: 8px;
          transition: all 0.3s ease;
        }
        .methodology-tag:hover {
          background: rgba(255, 255, 0, 0.2);
          transform: translateY(-2px);
        }
        /* Context buttons */
        .context-buttons {
          display: flex;
          gap: 8px;
          margin-top: 12px;
          justify-content: center;
        }
        .context-button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 4px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.7rem;
          transition: all 0.3s ease;
        }
        .entity-button {
          background: rgba(0, 255, 221, 0.1);
          border: 1px solid rgba(0, 255, 221, 0.3);
          color: #00ffdd;
        }
        .entity-button:hover {
          background: rgba(0, 255, 221, 0.2);
        }
        .entity-button.active {
          background: rgba(0, 255, 221, 0.3);
        }
        .endorsement-button {
          background: rgba(255, 0, 255, 0.1);
          border: 1px solid rgba(255, 0, 255, 0.3);
          color: #ff00ff;
        }
        .endorsement-button:hover {
          background: rgba(255, 0, 255, 0.2);
        }
        .endorsement-button.active {
          background: rgba(255, 0, 255, 0.3);
        }
        .methodology-button {
          background: rgba(255, 255, 0, 0.1);
          border: 1px solid rgba(255, 255, 0, 0.3);
          color: #fff740;
        }
        .methodology-button:hover {
          background: rgba(255, 255, 0, 0.2);
        }
        .methodology-button.active {
          background: rgba(255, 255, 0, 0.3);
        }
      `}</style>
      {/* Resolution Selector */}
      <div className="resolution-selector" data-id="element-600">
        <div className="text-[#a0c0e0] text-xs font-mono mr-2" data-id="element-601">RESOLUTION:</div>
        <button className={`resolution-button ${pixelDensity === 'low' ? 'active' : ''}`} onClick={() => changePixelDensity('low')} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-602">
          LOW
        </button>
        <button className={`resolution-button ${pixelDensity === 'medium' ? 'active' : ''}`} onClick={() => changePixelDensity('medium')} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-603">
          MED
        </button>
        <button className={`resolution-button ${pixelDensity === 'high' ? 'active' : ''}`} onClick={() => changePixelDensity('high')} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-604">
          HIGH
        </button>
      </div>
      <div className="pixel-info" data-id="element-605">
        <span data-id="element-606">
          {gridDimensions.width}×{gridDimensions.height} GRID • 9:16 RATIO
        </span>
        <span className="pixel-count" data-id="element-607">{pixelsFilled} NODES</span>
      </div>
      {/* Challenge Modal */}
      {showChallenge && <div className="challenge-modal" data-id="element-608">
          <div className="flex justify-between items-center mb-4" data-id="element-609">
            <h3 className="text-[#00FFDD] font-mono text-sm uppercase flex items-center gap-2" data-id="element-610">
              <NetworkIcon size={16} className="text-[#FF00FF]" data-id="element-611" />
              Networks: Orchid Mode
            </h3>
            <button className="text-[#a0c0e0] hover:text-[#00FFDD]" onClick={() => {
          playSound('click');
          setShowChallenge(false);
        }} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-612">
              <XIcon size={16} data-id="element-613" />
            </button>
          </div>
          <p className="text-[#f0f6ff] font-mono text-xs mb-4" data-id="element-614">
            Create a neural network representation of an orchid in the
            Biological Distortion framework. Your network will be analyzed for
            pattern coherence and structural integrity.
          </p>
          <div className="flex justify-between items-center" data-id="element-615">
            <button className="text-[#00FFDD] font-mono text-xs flex items-center gap-2 bg-[#0a1018] px-3 py-1.5 rounded border border-[#00FFDD]/30 hover:bg-[#00FFDD]/10 transition-all" onClick={toggleReferenceImage} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-616">
              <ImageIcon size={14} data-id="element-617" />
              View Reference
            </button>
            <button className="text-[#00FFDD] font-mono text-xs flex items-center gap-2 bg-[#0a1018] px-3 py-1.5 rounded border border-[#00FFDD]/30 hover:bg-[#00FFDD]/10 transition-all" onClick={() => {
          playSound('click');
          setShowChallenge(false);
        }} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-618">
              <NetworkIcon size={14} data-id="element-619" />
              Start Mapping
            </button>
          </div>
          {/* Interface corners */}
          <div className="interface-corner corner-tl" data-id="element-620"></div>
          <div className="interface-corner corner-tr" data-id="element-621"></div>
          <div className="interface-corner corner-bl" data-id="element-622"></div>
          <div className="interface-corner corner-br" data-id="element-623"></div>
        </div>}
      {/* Reference Image Modal */}
      {showReferenceImage && <div className="reference-modal" data-id="element-624">
          <div className="flex justify-between items-center mb-4" data-id="element-625">
            <h3 className="text-[#00FFDD] font-mono text-sm uppercase flex items-center gap-2" data-id="element-626">
              <ImageIcon size={16} className="text-[#FF00FF]" data-id="element-627" />
              Orchid Reference Database
            </h3>
            <button className="text-[#a0c0e0] hover:text-[#00FFDD]" onClick={toggleReferenceImage} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-628">
              <XIcon size={16} data-id="element-629" />
            </button>
          </div>
          <p className="text-[#f0f6ff] font-mono text-xs mb-2" data-id="element-630">
            Study these orchid specimens to inform your network structure:
          </p>
          <div className="reference-image-container" data-id="element-631">
            {orchidReferenceImages.map((image, index) => <img key={index} src={image} alt={`Orchid reference ${index + 1}`} className="reference-image" onMouseEnter={() => onCursorChange('image')} onMouseLeave={() => onCursorChange('default')} data-id="element-632" />)}
          </div>
          <p className="text-[#a0c0e0] font-mono text-xs mt-4" data-id="element-633">
            Focus on translating organic structures into coherent network
            pathways.
          </p>
          {/* Interface corners */}
          <div className="interface-corner corner-tl" data-id="element-634"></div>
          <div className="interface-corner corner-tr" data-id="element-635"></div>
          <div className="interface-corner corner-bl" data-id="element-636"></div>
          <div className="interface-corner corner-br" data-id="element-637"></div>
        </div>}
      {/* Canvas Container */}
      <div className="w-full overflow-hidden relative" ref={containerRef} data-id="element-638">
        {/* Symmetry mode indicator */}
        {showSymmetryGrid && <div className="symmetry-indicator" data-id="element-639">
            {symmetryMode.toUpperCase()} SYMMETRY
          </div>}
        {/* Interface corners */}
        <div className="interface-corner corner-tl" data-id="element-640"></div>
        <div className="interface-corner corner-tr" data-id="element-641"></div>
        <div className="interface-corner corner-bl" data-id="element-642"></div>
        <div className="interface-corner corner-br" data-id="element-643"></div>
        <canvas ref={canvasRef} className="drawing-canvas" onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} onTouchStart={e => {
        const touch = e.touches[0];
        const rect = e.target.getBoundingClientRect();
        const offsetX = touch.clientX - rect.left;
        const offsetY = touch.clientY - rect.top;
        const mouseEvent = {
          nativeEvent: {
            offsetX,
            offsetY
          }
        };
        startDrawing(mouseEvent);
      }} onTouchMove={e => {
        const touch = e.touches[0];
        const rect = e.target.getBoundingClientRect();
        const offsetX = touch.clientX - rect.left;
        const offsetY = touch.clientY - rect.top;
        const mouseEvent = {
          nativeEvent: {
            offsetX,
            offsetY
          }
        };
        draw(mouseEvent);
      }} onTouchEnd={stopDrawing} onMouseEnter={() => onCursorChange('hidden')} onMouseOut={() => onCursorChange('default')} data-id="element-644" />
        {/* Share Options Popup */}
        {showShareOptions && <div className="share-options" data-id="element-645">
            <button className="share-button share-button-twitter" onClick={shareToTwitter} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-646">
              <TwitterIcon size={16} data-id="element-647" />
              Share on Twitter
            </button>
            <button className="share-button share-button-facebook" onClick={shareToFacebook} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-648">
              <FacebookIcon size={16} data-id="element-649" />
              Share on Facebook
            </button>
            <button className="share-button share-button-copy" onClick={copyShareLink} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-650">
              <CopyIcon size={16} data-id="element-651" />
              Copy Link
            </button>
          </div>}
        {/* Copy Success Message */}
        {copySuccess && <div className="success-message" data-id="element-652">
            <CheckIcon size={14} data-id="element-653" />
            Link copied to clipboard
          </div>}
        {/* AI Feedback */}
        {showAIFeedback && <div className="ai-feedback" data-id="element-654">
            <BrainIcon size={14} data-id="element-655" />
            {aiSuggestion}
          </div>}
      </div>
      <div className="drawing-controls" data-id="element-656">
        {/* Drawing Tools */}
        <div className="tool-group" data-id="element-657">
          <button className={`drawing-button ${tool === 'pencil' ? 'active' : ''}`} onClick={() => handleToolChange('pencil')} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} aria-label="Node tool" data-id="element-658">
            <PencilIcon size={16} data-id="element-659" />
          </button>
          <button className={`drawing-button ${tool === 'eraser' ? 'active' : ''}`} onClick={() => handleToolChange('eraser')} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} aria-label="Eraser tool" data-id="element-660">
            <EraserIcon size={16} data-id="element-661" />
          </button>
        </div>
        {/* View Controls */}
        <div className="tool-group" data-id="element-662">
          <button className={`drawing-button ${showGrid ? 'active' : ''}`} onClick={toggleGrid} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} aria-label="Toggle grid" data-id="element-663">
            <GridIcon size={16} data-id="element-664" />
          </button>
          <button className="drawing-button" onClick={toggleReferenceImage} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} aria-label="Show reference image" data-id="element-665">
            <ImageIcon size={16} data-id="element-666" />
          </button>
          <button className={`drawing-button ${showSymmetryGrid ? 'active' : ''}`} onClick={toggleSymmetryGrid} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} aria-label="Toggle symmetry" data-id="element-667">
            <LayersIcon size={16} data-id="element-668" />
          </button>
          <button className={`drawing-button ${showParticles ? 'active' : ''}`} onClick={toggleParticles} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} aria-label="Toggle particles" data-id="element-669">
            <SparklesIcon size={16} data-id="element-670" />
          </button>
        </div>
        {/* Color Picker */}
        <div className="color-picker" data-id="element-671">
          {colors.map(clr => <div key={clr} className={`color-option ${color === clr ? 'active' : ''}`} style={{
          backgroundColor: clr
        }} onClick={() => handleColorSelect(clr)} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} aria-label={`Select ${clr} color`} data-id="element-672" />)}
        </div>
        {/* Filter Options */}
        <div className="filter-options" data-id="element-673">
          {filters.map(filter => <button key={filter.id} className={`filter-button ${activeFilter === filter.id ? 'active' : ''}`} onClick={() => applyFilter(filter.id)} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} aria-label={`Apply ${filter.name} filter`} data-id="element-674">
              {filter.icon}
              {filter.name}
            </button>)}
        </div>
        {/* History Controls */}
        <div className="tool-group" data-id="element-675">
          <button className={`drawing-button ${undoStack.length === 0 ? 'opacity-50' : ''}`} onClick={undo} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} aria-label="Undo" disabled={undoStack.length === 0} data-id="element-676">
            <RotateCcwIcon size={16} data-id="element-677" />
          </button>
          <button className={`drawing-button ${redoStack.length === 0 ? 'opacity-50' : ''}`} onClick={redo} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} aria-label="Redo" disabled={redoStack.length === 0} data-id="element-678">
            <RotateCwIcon size={16} data-id="element-679" />
          </button>
        </div>
        {/* Action Controls */}
        <div className="tool-group" data-id="element-680">
          <button className="drawing-button" onClick={clearCanvas} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} aria-label="Clear canvas" data-id="element-681">
            <RefreshCwIcon size={16} data-id="element-682" />
          </button>
          <button className="drawing-button" onClick={saveCanvas} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} aria-label="Save drawing" data-id="element-683">
            <SaveIcon size={16} data-id="element-684" />
          </button>
          <div className="relative" data-id="element-685">
            <button className="drawing-button" onClick={toggleShareOptions} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} aria-label="Share drawing" data-id="element-686">
              <ShareIcon size={16} data-id="element-687" />
            </button>
          </div>
        </div>
      </div>
      <div className="flex justify-between items-center mt-4" data-id="element-688">
        <div className="drawing-instruction" data-id="element-689">
          NETWORKS: ORCHID MODE • CREATE NEURAL PATHWAYS THAT MIMIC ORGANIC
          STRUCTURES
        </div>
        <button className="text-[#00FFDD] font-mono text-xs flex items-center gap-2 bg-[#0a1018] px-3 py-1.5 rounded border border-[#00FFDD]/30 hover:bg-[#00FFDD]/10 transition-all" onClick={completeChallenge} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-690">
          <BrainIcon size={14} data-id="element-691" />
          Analyze Network
        </button>
      </div>
      {/* Neural Analysis Section - Now with Entity Influence */}
      {neuralAnalysis && <div className="neural-analysis" data-id="element-692">
          <div className="neural-analysis-header" data-id="element-693">
            <div className="neural-analysis-title" data-id="element-694">
              <NetworkIcon size={16} data-id="element-695" />
              NETWORK ANALYSIS
            </div>
            {neuralProgress >= 100 && <div className="neural-analysis-score" data-id="element-696">{analysisScore}%</div>}
          </div>
          {/* Progress bar */}
          <div className="neural-progress" data-id="element-697">
            <div className="neural-progress-bar" style={{
          width: `${neuralProgress}%`
        }} data-id="element-698"></div>
          </div>
          {neuralProgress < 100 && <div className="text-[#a0c0e0] text-xs text-center" data-id="element-699">
              Analyzing network topology... {neuralProgress}%
            </div>}
          {neuralProgress >= 100 && <>
              <div className="neural-data-grid" data-id="element-700">
                {Object.entries(analysisData).map(([key, value]) => <div key={key} className="neural-data-item" data-id="element-701">
                    <div className="neural-data-label" data-id="element-702">
                      {key.replace(/([A-Z])/g, ' $1').toUpperCase()}
                    </div>
                    <div className="neural-data-value" data-id="element-703">{value}</div>
                  </div>)}
              </div>
            </>}
        </div>}
      {/* Context Buttons - Entity, Endorsements, Methodologies */}
      <div className="context-buttons" data-id="element-704">
        <button className={`context-button entity-button ${showEntityInfluence ? 'active' : ''}`} onClick={toggleEntityInfluence} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-705">
          <UserIcon size={14} data-id="element-706" />
          Entity Influence
        </button>
        <button className={`context-button endorsement-button ${showEndorsements ? 'active' : ''}`} onClick={toggleEndorsements} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-707">
          <MessageCircleIcon size={14} data-id="element-708" />
          Endorsements
        </button>
        <button className={`context-button methodology-button ${showMethodologies ? 'active' : ''}`} onClick={toggleMethodologies} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-709">
          <BookIcon size={14} data-id="element-710" />
          Methodologies
        </button>
      </div>
      {/* Entity Influence Panel */}
      {showEntityInfluence && <div className="entity-influence-panel" data-id="element-711">
          {avatar ? <>
              <div className="entity-header" data-id="element-712">
                <img src={avatar.image} alt={avatar.name} className="entity-avatar" style={{
            borderColor: avatar.accent
          }} data-id="element-713" />
                <div className="entity-info" data-id="element-714">
                  <div className="entity-name" style={{
              color: avatar.accent
            }} data-id="element-715">
                    {avatar.name}
                  </div>
                  <div className="entity-description" data-id="element-716">{avatar.description}</div>
                </div>
              </div>
              <div className="entity-influence-meter" data-id="element-717">
                <div className="entity-influence-value" style={{
            width: '75%',
            background: `linear-gradient(90deg, ${avatar.accent}, ${avatar.secondaryAccent})`
          }} data-id="element-718"></div>
              </div>
              <div className="entity-bonus" data-id="element-719">+15% NEURAL COHERENCE BOOST</div>
            </> : <div className="text-center py-4" data-id="element-720">
              <div className="text-[#a0c0e0] text-sm mb-2" data-id="element-721">
                No entity selected
              </div>
              <div className="text-[#00ffdd] text-xs" data-id="element-722">
                Select an entity from the avatar selection to enhance your
                orchid network.
              </div>
            </div>}
        </div>}
      {/* Endorsements Panel */}
      {showEndorsements && <div className="endorsement-panel" data-id="element-723">
          <div className="endorsement-quote" data-id="element-724">
            "
            {endorsements[Math.floor(Math.random() * endorsements.length)].quote}
            "
          </div>
          <div className="endorsement-author" data-id="element-725">
            —{' '}
            {endorsements[Math.floor(Math.random() * endorsements.length)].author}
          </div>
        </div>}
      {/* Methodologies Panel */}
      {showMethodologies && <div className="methodology-panel" data-id="element-726">
          {(() => {
        const methodology = methodologies[Math.floor(Math.random() * methodologies.length)];
        return <>
                <div className="methodology-name" data-id="element-727">{methodology.name}</div>
                <div className="methodology-description" data-id="element-728">
                  {methodology.description}
                </div>
                <div data-id="element-729">
                  {methodologies.map(m => <span key={m.name} className="methodology-tag" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-730">
                      {m.name}
                    </span>)}
                </div>
              </>;
      })()}
        </div>}
    </div>;
};